import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import type { ScopeUnitDto } from '@repo/shared/dtos/analytics/ScopeFilterDto';
import type { UsedExperienceDto } from '@repo/shared/dtos/analytics/ExperienceFilterDto';
import { useSecurityDomain } from '../../../../hooks/auth';
import { FILTER_CONFIGS } from '../constants/filterConfig';
import UnifiedFilterDropdown from './UnifiedFilterDropdown';
import DateRangePicker from './DateRangePicker';
import { AnalyticsCategory } from '../../config';
import { useIsMobile } from '../../../../hooks/useIsMobile';

interface FilterControlsProps {
  filters: BaseAnalyticsFilter;
  onFiltersChange: (filters: BaseAnalyticsFilter) => void;
  category?: AnalyticsCategory;
}

export default function FilterControls({
  filters,
  onFiltersChange,
  category,
}: FilterControlsProps) {
  const isMobile = useIsMobile();
  const { isAllowed: canSelectUnits } = useSecurityDomain({
    routeObject: 'analytics',
    routeItem: 'scopefilter',
    action: 'read',
  });

  const handleUnitSelectionChange = (unitIds: number[]) => {
    onFiltersChange({ ...filters, unitIds });
  };

  const handleExperienceSelectionChange = (experienceIds: number[]) => {
    onFiltersChange({
      ...filters,
      experienceIds,
      experienceTitle: undefined,
    });
  };

  const unitsConfig = FILTER_CONFIGS.units;
  const experiencesConfig = FILTER_CONFIGS.experiences;

  const UnitsIcon = unitsConfig.icon;
  const ExperiencesIcon = experiencesConfig.icon;

  return (
    <div className={`relative z-10 flex w-full items-center ${isMobile ? 'flex-col gap-3' : 'flex-row gap-4'}`}>
      <div className={`flex flex-shrink-0 ${isMobile ? 'justify-center' : ''}`}>
        {canSelectUnits && (
          <UnifiedFilterDropdown<ScopeUnitDto>
            title={unitsConfig.title}
            icon={<UnitsIcon size={16} />}
            selectedIds={filters.unitIds || []}
            onSelectionChange={handleUnitSelectionChange}
            endpoint={unitsConfig.endpoint}
            getId={unitsConfig.getId}
            getDisplayName={unitsConfig.getDisplayName}
            variant={unitsConfig.variant}
          />
        )}
      </div>

      <div className={`flex ${isMobile ? 'justify-center' : 'flex-1 justify-center'}`}>
        <DateRangePicker filters={filters} onFiltersChange={onFiltersChange} category={category} />
      </div>

      <div className={`flex flex-shrink-0 ${isMobile ? 'justify-center' : ''}`}>
        <UnifiedFilterDropdown<UsedExperienceDto>
          title={experiencesConfig.title}
          icon={<ExperiencesIcon size={16} />}
          selectedIds={filters.experienceIds || []}
          onSelectionChange={handleExperienceSelectionChange}
          endpoint={experiencesConfig.endpoint}
          getId={experiencesConfig.getId}
          getDisplayName={experiencesConfig.getDisplayName}
          variant={experiencesConfig.variant}
        />
      </div>
    </div>
  );
}
